// Brush settings for the foliage tool.
//
// Shown only while the tool is armed. Species are picked individually rather
// than coming from a fixed blend: painting a flower meadow and painting a grass
// verge are different jobs, and a preset mix means every painted area comes out
// looking like every other one.
//
// Thumbnails are cut straight from the foliage atlas with background-position
// rather than being separate icon files. The atlas is already loaded for
// rendering, so the swatches cost nothing extra and — more usefully — they
// cannot drift out of sync with what the brush actually plants.

import React from 'react';
import { useWorldStore } from '../stores/worldStore';
import { FOLIAGE_SPECIES, ATLAS_COLS, ATLAS_ROWS } from './FoliageLayer';

/** Ground-cover species. Canopy is excluded — it belongs on a trunk, not underfoot. */
const PICKABLE = FOLIAGE_SPECIES
  .map((s, i) => ({ ...s, index: i }))
  .filter((s) => s.id !== 'canopy');

function tileStyle(tile: number): React.CSSProperties {
  const col = tile % ATLAS_COLS;
  const row = Math.floor(tile / ATLAS_COLS);
  return {
    backgroundImage: 'url(/textures/foliage/atlas.png)',
    backgroundSize: `${ATLAS_COLS * 100}% ${ATLAS_ROWS * 100}%`,
    backgroundPosition: `${(col / (ATLAS_COLS - 1)) * 100}% ${(row / (ATLAS_ROWS - 1)) * 100}%`,
    backgroundRepeat: 'no-repeat',
  };
}

export const FoliageBrushPanel: React.FC = () => {
  const buildTool = useWorldStore((s) => s.buildTool);
  const brush = useWorldStore((s) => s.foliageBrush);
  const setBrush = useWorldStore((s) => s.setFoliageBrush);
  const undo = useWorldStore((s) => s.undoFoliageStroke);
  const clearAll = useWorldStore((s) => s.clearFoliageStrokes);
  const strokes = useWorldStore((s) => s.foliageStrokes);

  if (buildTool !== 'foliage') return null;

  const toggle = (index: number) => {
    const has = brush.species.includes(index);
    // Never leave the selection empty — an empty brush silently plants nothing,
    // which reads as the tool being broken.
    const next = has ? brush.species.filter((i) => i !== index) : [...brush.species, index];
    setBrush({ species: next.length ? next : [index] });
  };

  return (
    <div
      style={{
        position: 'absolute', bottom: 152, left: '50%', transform: 'translateX(-50%)',
        background: 'color-mix(in srgb, var(--gx-surface) 82%, transparent)',
        backdropFilter: 'blur(20px)',
        border: '1px solid var(--gx-border)', borderRadius: 14,
        padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10,
        color: 'var(--gx-text)', fontSize: 12, zIndex: 40,
        boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
      }}
      onPointerDown={(e) => e.stopPropagation()}
      onPointerMove={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ letterSpacing: 1, fontSize: 10, opacity: 0.6 }}>PLANT</span>
        <button
          onClick={() => setBrush({ trees: false, erase: false })}
          style={modeBtn(!brush.trees && !brush.erase)}
        >Ground cover</button>
        <button
          onClick={() => setBrush({ trees: true, erase: false })}
          style={modeBtn(brush.trees && !brush.erase)}
        >Trees</button>
        <button
          onClick={() => setBrush({ erase: !brush.erase })}
          style={{ ...modeBtn(brush.erase), ...(brush.erase ? eraseOn : {}) }}
        >Erase</button>
      </div>

      {!brush.trees && !brush.erase && (
        <div style={{ display: 'flex', gap: 8 }}>
          {PICKABLE.map((sp) => {
            const on = brush.species.includes(sp.index);
            return (
              <button
                key={sp.id}
                onClick={() => toggle(sp.index)}
                title={sp.label}
                style={{
                  width: 56, padding: 0, cursor: 'pointer',
                  background: on ? 'rgba(0,194,179,0.16)' : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${on ? 'rgba(0,194,179,0.55)' : 'var(--gx-border)'}`,
                  borderRadius: 10, overflow: 'hidden',
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                }}
              >
                <span style={{ ...tileStyle(sp.tile), width: 52, height: 44, opacity: on ? 1 : 0.55 }} />
                <span style={{
                  fontSize: 10, padding: '2px 0 4px',
                  color: on ? '#7ff0e6' : 'rgba(255,255,255,0.6)',
                }}>{sp.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {brush.erase && (
        <div style={{ fontSize: 11, opacity: 0.65, maxWidth: 330 }}>
          Drag over plants to remove them. Erasing is recorded as its own stroke,
          so Undo brings them back and painting over the patch refills it.
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <label style={row}>
          <span style={{ opacity: 0.6 }}>Size</span>
          <input
            type="range" min="0.5" max="15" step="0.5" value={brush.radius}
            onChange={(e) => setBrush({ radius: parseFloat(e.target.value) })}
            style={{ width: 90 }}
          />
          <span style={val}>{brush.radius.toFixed(1)}m</span>
        </label>
        {!brush.erase && (
          <label style={row}>
            <span style={{ opacity: 0.6 }}>Density</span>
            <input
              type="range" min="0.2" max="6" step="0.1" value={brush.density}
              onChange={(e) => setBrush({ density: parseFloat(e.target.value) })}
              style={{ width: 90 }}
            />
            <span style={val}>{brush.density.toFixed(1)}</span>
          </label>
        )}
        <button
          onClick={undo}
          disabled={!strokes.length}
          style={{
            padding: '5px 10px', borderRadius: 8, cursor: strokes.length ? 'pointer' : 'default',
            background: 'rgba(255,255,255,0.05)', border: '1px solid var(--gx-border)',
            color: strokes.length ? 'var(--gx-text)' : 'rgba(255,255,255,0.3)', fontSize: 11,
          }}
        >Undo ({strokes.length})</button>
        <button
          onClick={() => {
            // Confirm because this cannot be undone stroke-by-stroke.
            if (strokes.length && window.confirm(`Remove all ${strokes.length} foliage strokes from this world?`)) clearAll();
          }}
          disabled={!strokes.length}
          style={{
            padding: '5px 10px', borderRadius: 8, cursor: strokes.length ? 'pointer' : 'default',
            background: 'rgba(255,107,107,0.10)', border: '1px solid rgba(255,107,107,0.3)',
            color: strokes.length ? '#ff9b9b' : 'rgba(255,255,255,0.3)', fontSize: 11,
          }}
        >Clear all</button>
      </div>
    </div>
  );
};

const eraseOn: React.CSSProperties = {
  background: 'rgba(255,107,107,0.18)',
  border: '1px solid rgba(255,107,107,0.55)',
  color: '#ff9b9b',
};

const row: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6 };
const val: React.CSSProperties = { minWidth: 34, textAlign: 'right', color: '#7ff0e6', fontSize: 11 };

function modeBtn(on: boolean): React.CSSProperties {
  return {
    padding: '5px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 11,
    background: on ? 'rgba(0,194,179,0.16)' : 'rgba(255,255,255,0.05)',
    border: `1px solid ${on ? 'rgba(0,194,179,0.5)' : 'var(--gx-border)'}`,
    color: on ? '#7ff0e6' : 'rgba(255,255,255,0.75)',
  };
}

export default FoliageBrushPanel;
